// IndexedDB wrapper using the idb library.
// Provides persistent storage for rides, OAuth tokens, and app metadata.

import { openDB } from 'idb';

const DB_NAME = 'cycling-dashboard';
const DB_VERSION = 1;

let _db = null;

async function getDB() {
    if (_db) return _db;
    _db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains('rides')) {
                const store = db.createObjectStore('rides', { keyPath: 'id' });
                store.createIndex('by_date', 'date');
            }
            if (!db.objectStoreNames.contains('tokens')) {
                db.createObjectStore('tokens', { keyPath: 'key' });
            }
            if (!db.objectStoreNames.contains('meta')) {
                db.createObjectStore('meta', { keyPath: 'key' });
            }
        }
    });
    return _db;
}

export async function isAvailable() {
    try {
        await getDB();
        return true;
    } catch {
        return false;
    }
}

export async function getAllRides() {
    const db = await getDB();
    return db.getAllFromIndex('rides', 'by_date');
}

export async function getLatestRideDate() {
    const db = await getDB();
    const cursor = await db
        .transaction('rides')
        .objectStore('rides')
        .index('by_date')
        .openCursor(null, 'prev');
    return cursor ? cursor.value.date : null;
}

// Content-based dedup key — catches the same physical ride arriving from
// different sources under different ids (e.g. Strava export vs. Wahoo sync).
// Date is normalised to YYYY-MM-DD so sources that emit full ISO timestamps
// (Wahoo) dedup against sources that emit date-only (file import).
function _dedupKey(ride) {
    return `${String(ride.date).slice(0, 10)}|${Number(ride.distance_km).toFixed(1)}`;
}

export async function saveRides(rides) {
    const db = await getDB();
    const tx = db.transaction('rides', 'readwrite');
    const existing = await tx.store.getAll();
    const keyToId = new Map(existing.map(r => [_dedupKey(r), r.id]));
    await Promise.all(rides.map(ride => {
        const existingId = keyToId.get(_dedupKey(ride));
        if (existingId != null && existingId !== ride.id) return;
        return tx.store.put(ride);
    }));
    await tx.done;
}

export async function clearAllRides() {
    const db = await getDB();
    await db.clear('rides');
}

// Single-ride upsert for manual edits / entries. Unlike saveRides this is a
// direct put with no content-dedup — the user's edit is authoritative.
export async function putRide(ride) {
    const db = await getDB();
    await db.put('rides', ride);
}

export async function deleteRide(id) {
    const db = await getDB();
    await db.delete('rides', id);
}

export async function getRideCount() {
    const db = await getDB();
    return db.count('rides');
}

export async function getMeta(key) {
    const db = await getDB();
    const entry = await db.get('meta', key);
    return entry ? entry.value : null;
}

export async function setMeta(key, value) {
    const db = await getDB();
    await db.put('meta', { key, value });
}

export async function getToken(key) {
    const db = await getDB();
    return (await db.get('tokens', key)) || null;
}

export async function setToken(data) {
    // data must include a 'key' field
    const db = await getDB();
    await db.put('tokens', data);
}

export async function deleteToken(key) {
    const db = await getDB();
    await db.delete('tokens', key);
}
